/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * FeedbackInboxPage -- R13 web Round 1.
 *
 * Owner-area feedback inbox. Filters by status; counts header shows
 * the 4-state breakdown. Click opens detail.
 *
 * Also hosts the "Log feedback" entry point (desktop idiom):
 * clicking a row's "Create contact from this" is handled on the
 * detail page.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import connectApi from '../../services/connectApi';

const SKD_CYAN_DARK = '#0e7490';

export default function FeedbackInboxPage() {
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({ new: 0, acknowledged: 0, in_progress: 0, resolved: 0 });
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await connectApi.listFeedback({ status });
      setRows(data.inbox || []);
      setCounts(data.counts || { new: 0, acknowledged: 0, in_progress: 0, resolved: 0 });
      setErr(null);
    } catch (e) {
      setErr(e.message || 'Could not load feedback.');
      setRows([]);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const totalAll = counts.new + counts.acknowledged + counts.in_progress + counts.resolved;

  return (
    <section>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
        <h1 style={styles.h1}>Feedback</h1>
      </div>

      <div style={styles.statusBar}>
        <StatusChip label="All" value="" active={status === ''}            count={totalAll}          onClick={setStatus} />
        <StatusChip label="New" value="new" active={status === 'new'}       count={counts.new}        onClick={setStatus} />
        <StatusChip label="Acknowledged" value="acknowledged" active={status === 'acknowledged'} count={counts.acknowledged} onClick={setStatus} />
        <StatusChip label="In progress"  value="in_progress"  active={status === 'in_progress'}  count={counts.in_progress}  onClick={setStatus} />
        <StatusChip label="Resolved"     value="resolved"     active={status === 'resolved'}     count={counts.resolved}     onClick={setStatus} />
      </div>

      {err && <div style={styles.errorBox}>{err}</div>}
      {rows === null && <div style={styles.muted}>Loading…</div>}
      {rows && rows.length === 0 && (
        <div style={styles.emptyCard}>
          No feedback in this view.{status ? ' Try another status.' : ''}
        </div>
      )}
      {rows && rows.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((r) => (
            <Link key={r.id} to={`/connect/feedback/${encodeURIComponent(r.id)}`} style={styles.row}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
                  {_prettyType(r.type)}
                  {r.step_title ? <span style={{ color: '#64748b', fontWeight: 400 }}> &middot; {r.step_title}</span> : null}
                </div>
                <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>
                  {(r.contact && r.contact.full_name) || <em style={{ color: '#94a3b8' }}>(unknown contact)</em>}
                  {r.contact && r.contact.organisation ? ` · ${r.contact.organisation}` : ''}
                </div>
                {r.text_preview && (
                  <div style={{ color: '#334155', fontSize: 13, marginTop: 4 }}>{r.text_preview}</div>
                )}
                {r.offering_name && (
                  <div style={{ color: '#1d4ed8', fontSize: 12, marginTop: 4 }}>
                    {r.programme_name ? `${r.offering_name} · ${r.programme_name}` : r.offering_name}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                <StatusPill status={r.status} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>via {r.source}</span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                  {new Date(r.created_at).toLocaleDateString()}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function StatusChip({ label, value, active, count, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', borderRadius: 999,
        background: active ? '#0e7490' : 'white',
        color: active ? 'white' : '#334155',
        border: `1px solid ${active ? '#0e7490' : '#cbd5e1'}`,
        fontSize: 13, fontWeight: 500, cursor: 'pointer'
      }}
    >
      {label}
      <span style={{
        fontSize: 11, fontWeight: 700,
        padding: '1px 6px', borderRadius: 999,
        background: active ? 'rgba(255,255,255,0.2)' : '#f1f5f9',
        color: active ? 'white' : '#64748b'
      }}>{count}</span>
    </button>
  );
}
function StatusPill({ status }) {
  const colors = {
    new:          { bg: '#fef3c7', fg: '#92400e' },
    acknowledged: { bg: '#dbeafe', fg: '#1e40af' },
    in_progress:  { bg: '#e0f2fe', fg: '#0369a1' },
    resolved:     { bg: '#dcfce7', fg: '#166534' }
  }[status] || { bg: '#f1f5f9', fg: '#334155' };
  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 999,
      background: colors.bg, color: colors.fg, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.03em'
    }}>{status}</span>
  );
}

function _prettyType(t) {
  const map = {
    goals: 'Goals',
    improve: 'Suggestion',
    request_call: 'Requested a call',
    send_more_details: 'Asked for more details',
    wrong_person: 'Not the right person',
    not_interested: 'Not interested',
    pilot_interest: 'Pilot interest',
    step_reaction: 'Step reaction'
  };
  return map[t] || (t || '').replace(/_/g, ' ');
}

const styles = {
  h1: { fontSize: 22, color: '#0f172a', margin: 0 },
  statusBar: {
    display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14
  },
  row: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '12px 14px', borderRadius: 10,
    background: 'white', border: '1px solid #e2e8f0',
    color: '#0f172a', textDecoration: 'none'
  },
  emptyCard: {
    padding: 20, background: 'white', border: '1px solid #e2e8f0', borderRadius: 10,
    color: '#64748b', fontSize: 14
  },
  muted: { color: '#94a3b8', padding: 20, textAlign: 'center' },
  errorBox: {
    padding: 10, background: '#fef2f2', color: '#991b1b',
    borderRadius: 8, border: '1px solid #fecaca', marginBottom: 10, fontSize: 13
  }
};
